/**
 * API-слой вкладки «Оплаты»: react-query хуки на все эндпоинты PaymentsController.
 *
 * Контракты — из фундамента (lib/types.ts, lib/apiClient.ts, lib/queryClient.ts):
 *  - деньги — строки-копейки (*Minor); форматирование только через money.ts;
 *  - № транша — number;
 *  - мутации PATCH/DELETE — с If-Match: version (apiClient сам оборачивает в кавычки);
 *  - создающие POST (payments/transactions/template/finalize) — apiClient сам добавляет Idempotency-Key;
 *  - оптимистичные апдейты с откатом; 409 VERSION_CONFLICT → рефетч; 409 BUDGET_MISMATCH → отдать вызывающему.
 *
 * Пути точно соответствуют backend/src/payments/payments.controller.ts.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiClient, ApiError } from '../../lib/apiClient';
import { queryKeys } from '../../lib/queryClient';
import type {
  BulkTemplateRequest,
  BulkTemplateResponse,
  CreatePaymentRequest,
  CreateTransactionRequest,
  CreateTransactionResponse,
  DeletePaymentResponse,
  EventLogDto,
  EventsQuery,
  FinalizeResponse,
  HistoryEntryDto,
  HistoryQuery,
  ListPaymentsQuery,
  PageEnvelope,
  PaymentDto,
  SettingsResponse,
  StatsDto,
  UpdatePaymentRequest,
  ValidationResult,
} from '../../lib/types';

// ---------------------------------------------------------------------------
// Общий скоуп (accountId + dealId прокидываются сверху из PaymentsTab)
// ---------------------------------------------------------------------------

export interface DealScope {
  accountId: string;
  dealId: string;
}

type PaymentsPage = PageEnvelope<PaymentDto>;

/** Ключ списка платежей (единый на весь модуль). */
function paymentsKey(scope: DealScope): readonly unknown[] {
  return queryKeys.payments(scope.accountId, scope.dealId);
}
function statsKey(scope: DealScope): readonly unknown[] {
  return queryKeys.stats(scope.accountId, scope.dealId);
}
function eventsKey(scope: DealScope): readonly unknown[] {
  return queryKeys.events(scope.accountId, scope.dealId);
}
function historyKey(scope: DealScope, paymentId?: string): readonly unknown[] {
  return queryKeys.history(scope.accountId, scope.dealId, paymentId);
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Список платежей сделки. Грузим с includeTransactions=true, чтобы клиент мог определить
 * «есть транзакции» (замок оплаченного) и показать детали в диалоге оплаты.
 */
export function usePayments(scope: DealScope): UseQueryResult<PaymentsPage, ApiError> {
  return useQuery<PaymentsPage, ApiError>({
    queryKey: paymentsKey(scope),
    queryFn: ({ signal }) =>
      apiClient.get<PaymentsPage>(`/api/deals/${scope.dealId}/payments`, {
        query: { includeTransactions: true, sort: 'number', limit: 200 } satisfies ListPaymentsQuery,
        signal,
      }),
  });
}

/** Агрегаты вкладки (карточки-показатели, баннер бюджета). */
export function useStats(scope: DealScope): UseQueryResult<StatsDto, ApiError> {
  return useQuery<StatsDto, ApiError>({
    queryKey: statsKey(scope),
    queryFn: ({ signal }) => apiClient.get<StatsDto>(`/api/deals/${scope.dealId}/stats`, { signal }),
  });
}

/** Лента событий (таймлайн «История оплат»). */
export function useEvents(scope: DealScope, query?: EventsQuery): UseQueryResult<PageEnvelope<EventLogDto>, ApiError> {
  return useQuery<PageEnvelope<EventLogDto>, ApiError>({
    queryKey: eventsKey(scope),
    queryFn: ({ signal }) =>
      apiClient.get<PageEnvelope<EventLogDto>>(`/api/deals/${scope.dealId}/events`, {
        query: { limit: query?.limit ?? 100, ...(query?.type ? { type: query.type } : {}) },
        signal,
      }),
    staleTime: 60_000,
  });
}

/**
 * Пополевая история изменений (выезжающая панель). Скоупится по конкретному траншу.
 * Включается только когда paymentId задан (открыт Drawer).
 */
export function useHistory(scope: DealScope, paymentId: string | null): UseQueryResult<PageEnvelope<HistoryEntryDto>, ApiError> {
  return useQuery<PageEnvelope<HistoryEntryDto>, ApiError>({
    queryKey: historyKey(scope, paymentId ?? undefined),
    enabled: paymentId !== null,
    queryFn: ({ signal }) => {
      const query: HistoryQuery = { limit: 100 };
      if (paymentId !== null) query.paymentId = paymentId;
      return apiClient.get<PageEnvelope<HistoryEntryDto>>(`/api/deals/${scope.dealId}/history`, {
        query: { ...query },
        signal,
      });
    },
  });
}

/** Настройки аккаунта (пресеты графика для TemplatePicker, currency и т.п.). */
export function useSettings(accountId: string): UseQueryResult<SettingsResponse, ApiError> {
  return useQuery<SettingsResponse, ApiError>({
    queryKey: queryKeys.settings(accountId),
    queryFn: ({ signal }) => apiClient.get<SettingsResponse>('/api/settings', { signal }),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Мутации
// ---------------------------------------------------------------------------

/** После мутации, меняющей суммы/статусы, освежаем список + агрегаты + ленту. */
function invalidateDeal(qc: QueryClient, scope: DealScope): void {
  void qc.invalidateQueries({ queryKey: paymentsKey(scope) });
  void qc.invalidateQueries({ queryKey: statsKey(scope) });
  void qc.invalidateQueries({ queryKey: eventsKey(scope) });
}

export interface CreatePaymentVars {
  body: CreatePaymentRequest;
}

/** Создание транша (POST /payments). Idempotency-Key ставит apiClient. */
export function useCreatePayment(scope: DealScope): UseMutationResult<PaymentDto, ApiError, CreatePaymentVars> {
  const qc = useQueryClient();
  return useMutation<PaymentDto, ApiError, CreatePaymentVars>({
    mutationFn: ({ body }) => apiClient.post<PaymentDto>(`/api/deals/${scope.dealId}/payments`, body),
    onSuccess: () => invalidateDeal(qc, scope),
  });
}

export interface UpdatePaymentVars {
  id: string;
  version: number;
  patch: UpdatePaymentRequest;
}

interface UpdateContext {
  // Явно `| undefined` (не optional): под exactOptionalPropertyTypes возврат { prev: undefined } из onMutate
  // не присваивается optional-полю. Так контекст корректно типизируется при отсутствии кэша.
  prev: PaymentsPage | undefined;
}

/**
 * Инлайн-правка транша (PATCH). Оптимистично патчим кэш; при 409 (конфликт версий/чужое изменение)
 * откатываем и рефетчим. Вызывающий может дополнительно среагировать на error (тост).
 */
export function useUpdatePayment(
  scope: DealScope,
): UseMutationResult<PaymentDto, ApiError, UpdatePaymentVars, UpdateContext> {
  const qc = useQueryClient();
  const key = paymentsKey(scope);

  return useMutation<PaymentDto, ApiError, UpdatePaymentVars, UpdateContext>({
    mutationFn: ({ id, version, patch }) =>
      apiClient.patch<PaymentDto>(`/api/payments/${id}`, patch, { ifMatch: version }),

    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PaymentsPage>(key);
      if (prev) {
        qc.setQueryData<PaymentsPage>(key, {
          ...prev,
          data: prev.data.map((p) => (p.id === id ? { ...p, ...patch } : p)),
        });
      }
      return { prev };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      // 409 (VERSION_CONFLICT) и прочее — перечитываем, чтобы взять свежую version.
      void qc.invalidateQueries({ queryKey: key });
    },

    onSuccess: (updated) => {
      // Кладём серверный вид (свежая version/status/paid) в кэш, чтобы не терять его до инвалидации.
      const prev = qc.getQueryData<PaymentsPage>(key);
      if (prev) {
        qc.setQueryData<PaymentsPage>(key, {
          ...prev,
          data: prev.data.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
        });
      }
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: statsKey(scope) });
      void qc.invalidateQueries({ queryKey: eventsKey(scope) });
    },
  });
}

export interface DeletePaymentVars {
  id: string;
  version: number;
}

interface DeleteContext {
  prev: PaymentsPage | undefined;
}

/**
 * Удаление/отмена транша (DELETE, отдаёт CANCELLED). If-Match обязателен.
 * Оптимистично убираем из списка; при ошибке откат + рефетч.
 */
export function useDeletePayment(
  scope: DealScope,
): UseMutationResult<DeletePaymentResponse, ApiError, DeletePaymentVars, DeleteContext> {
  const qc = useQueryClient();
  const key = paymentsKey(scope);

  return useMutation<DeletePaymentResponse, ApiError, DeletePaymentVars, DeleteContext>({
    mutationFn: ({ id, version }) =>
      apiClient.delete<DeletePaymentResponse>(`/api/payments/${id}`, { ifMatch: version }),

    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PaymentsPage>(key);
      if (prev) {
        qc.setQueryData<PaymentsPage>(key, { ...prev, data: prev.data.filter((p) => p.id !== id) });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      void qc.invalidateQueries({ queryKey: key });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: statsKey(scope) });
      void qc.invalidateQueries({ queryKey: eventsKey(scope) });
    },
  });
}

export interface AddTransactionVars {
  paymentId: string;
  body: CreateTransactionRequest;
  /** Стабильный ключ идемпотентности на сессию диалога (повтор по 401-refresh не создаст 2-ю оплату). */
  idempotencyKey?: string;
}

/** Внесение оплаты (полной/частичной): POST /payments/:id/transactions. */
export function useAddTransaction(
  scope: DealScope,
): UseMutationResult<CreateTransactionResponse, ApiError, AddTransactionVars> {
  const qc = useQueryClient();
  return useMutation<CreateTransactionResponse, ApiError, AddTransactionVars>({
    mutationFn: ({ paymentId, body, idempotencyKey }) =>
      apiClient.post<CreateTransactionResponse>(`/api/payments/${paymentId}/transactions`, body, {
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    onSuccess: () => invalidateDeal(qc, scope),
  });
}

export interface ApplyTemplateVars {
  body: BulkTemplateRequest;
  idempotencyKey?: string;
}

/** Применение шаблона графика: POST /deals/:dealId/payments/template. */
export function useApplyTemplate(
  scope: DealScope,
): UseMutationResult<BulkTemplateResponse, ApiError, ApplyTemplateVars> {
  const qc = useQueryClient();
  return useMutation<BulkTemplateResponse, ApiError, ApplyTemplateVars>({
    mutationFn: ({ body, idempotencyKey }) =>
      apiClient.post<BulkTemplateResponse>(`/api/deals/${scope.dealId}/payments/template`, body, {
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    onSuccess: () => invalidateDeal(qc, scope),
  });
}

/** Сверка Σ траншей vs бюджет (не strict) — для баннера соответствия. POST /payments/validate. */
export function useValidate(scope: DealScope): UseMutationResult<ValidationResult, ApiError, void> {
  return useMutation<ValidationResult, ApiError, void>({
    mutationFn: () => apiClient.post<ValidationResult>(`/api/deals/${scope.dealId}/payments/validate`),
  });
}

/** Завершение настройки плана (strict, право manage_settings). POST /deals/:dealId/finalize. */
export function useFinalize(scope: DealScope): UseMutationResult<FinalizeResponse, ApiError, void> {
  const qc = useQueryClient();
  return useMutation<FinalizeResponse, ApiError, void>({
    mutationFn: () => apiClient.post<FinalizeResponse>(`/api/deals/${scope.dealId}/finalize`),
    onSuccess: () => invalidateDeal(qc, scope),
  });
}

// ---------------------------------------------------------------------------
// Экспорт (blob-скачивание)
// ---------------------------------------------------------------------------

export type ExportFormat = 'csv' | 'xlsx';
export type ExportScopeName = 'payments' | 'history' | 'events';

export interface ExportVars {
  format: ExportFormat;
  scope?: ExportScopeName;
}

/**
 * Экспорт в файл. Идёт через apiClient (Bearer + 401-refresh), ответ — Blob;
 * сохранение файла инициируем программно (без <a href>, т.к. нужен заголовок Authorization).
 */
export function useExport(scope: DealScope): UseMutationResult<void, ApiError, ExportVars> {
  return useMutation<void, ApiError, ExportVars>({
    mutationFn: async ({ format, scope: exportScope = 'payments' }) => {
      const blob = await apiClient.get<Blob>(`/api/deals/${scope.dealId}/export`, {
        query: { format, scope: exportScope },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments_${exportScope}_${scope.dealId}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}
